import { useRef, useState } from 'react';
import { Loader2, Eye, EyeOff, CheckCircle, Camera, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import { BACKEND_URL } from '../lib/backend';
import { toast } from 'sonner';

const AVAILABLE_GENRES = [
  { value: 'dancehall', label: 'Dancehall' },
  { value: 'reggae', label: 'Reggae' },
  { value: 'soca', label: 'Soca' },
  { value: 'afrobeats', label: 'Afrobeats' },
  { value: 'hiphop', label: 'Hip Hop' },
  { value: 'rnb', label: 'R&B' },
  { value: 'gospel', label: 'Gospel' },
  { value: 'other', label: 'Other' }
];

export function SetupForm({ formData, setFormData, artistInfo, handleSubmit, saving, showPassword, setShowPassword, token }) {
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const previewSrc = formData.image_url
    ? (formData.image_url.startsWith('/') ? `${BACKEND_URL}${formData.image_url}` : formData.image_url)
    : null;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.uploadSetupPhoto(token, fd);
      setFormData((prev) => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload that image');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleGenreToggle = (genre) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre]
    }));
  };

  return (
    <motion.form {...fadeInUp} transition={{ delay: 0.1 }} onSubmit={handleSubmit} className="bg-surface border border-[#262626] rounded-2xl p-6 sm:p-8 space-y-6">
      {/* Account Setup */}
      <div className="space-y-4">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 bg-lime rounded-full flex items-center justify-center text-black text-sm font-bold">1</span>
          Account Setup
        </h2>
        <div className="space-y-2">
          <Label htmlFor="email">Email {!artistInfo?.artist_email && '*'}</Label>
          {artistInfo?.artist_email ? (
            <Input value={artistInfo.artist_email} disabled className="input-dark opacity-60" />
          ) : (
            <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="input-dark" placeholder="Enter your email address" required data-testid="setup-email" />
          )}
          {!artistInfo?.artist_email && <p className="text-xs text-[#666]">This will be your login email</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password *</Label>
          <div className="relative">
            <Input id="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="input-dark pr-10" placeholder="Create a password (min 8, upper+lower+number)" required data-testid="setup-password" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password *</Label>
          <Input id="confirmPassword" type="password" value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className="input-dark" placeholder="Confirm your password" required data-testid="setup-confirm-password" />
        </div>
      </div>

      {/* Profile Info */}
      <div className="space-y-4 pt-4 border-t border-[#262626]">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 bg-lime rounded-full flex items-center justify-center text-black text-sm font-bold">2</span>
          Profile Information
        </h2>
        <div className="space-y-2">
          <Label htmlFor="bio">Bio</Label>
          <Textarea id="bio" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} className="input-dark min-h-[100px]" placeholder="Tell buyers about yourself and your music..." data-testid="setup-bio" />
        </div>
        <div className="space-y-2">
          <Label>Genres</Label>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_GENRES.map(genre => (
              <button key={genre.value} type="button" onClick={() => handleGenreToggle(genre.value)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${formData.genres.includes(genre.value) ? 'bg-lime text-black border-lime' : 'bg-transparent text-[#A1A1AA] border-[#404040] hover:border-lime'}`} data-testid={`setup-genre-${genre.value}`}>
                {genre.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#666]">Select the genres that best describe your music</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="price">Price (USD)</Label>
            <Input id="price" type="number" step="0.01" min="0" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="input-dark" placeholder="e.g. 100.00" data-testid="setup-price" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="delivery_days">Delivery Days</Label>
            <Input id="delivery_days" type="number" min="1" value={formData.delivery_days} onChange={(e) => setFormData({ ...formData, delivery_days: e.target.value })} className="input-dark" placeholder="e.g. 14" data-testid="setup-delivery" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Profile Photo</Label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-[#1a1a1a] border-2 border-[#262626] overflow-hidden flex items-center justify-center shrink-0" data-testid="setup-photo-preview">
              {previewSrc ? (
                <img src={previewSrc} alt="Profile preview" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-[#555]" />
              )}
            </div>
            <div className="space-y-1.5">
              <Button
                type="button" variant="outline"
                className="border-[#404040] hover:border-lime"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="setup-photo-upload-btn"
              >
                {uploadingPhoto ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading…</>) : (<><Camera className="w-4 h-4 mr-2" />Upload a photo</>)}
              </Button>
              <p className="text-xs text-[#666]">JPEG, PNG, WebP or GIF — up to 5MB</p>
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} data-testid="setup-photo-input" />
          </div>
          <div className="pt-1">
            <Label htmlFor="image_url" className="text-xs text-[#A1A1AA]">…or paste a link to your photo</Label>
            <Input id="image_url" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} className="input-dark mt-1.5" placeholder="https://..." data-testid="setup-image" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="spotify_url">Spotify URL (optional)</Label>
          <Input id="spotify_url" value={formData.spotify_url} onChange={(e) => setFormData({ ...formData, spotify_url: e.target.value })} className="input-dark" placeholder="https://open.spotify.com/artist/..." data-testid="setup-spotify" />
        </div>
      </div>

      {/* Payout Settings */}
      <div className="space-y-4 pt-4 border-t border-[#262626]">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 bg-lime rounded-full flex items-center justify-center text-black text-sm font-bold">3</span>
          Payout Method
        </h2>
        <RadioGroup value={formData.payout_method} onValueChange={(value) => setFormData({ ...formData, payout_method: value })} className="space-y-3">
          <div className="flex items-center space-x-3 p-4 bg-surface-highlight rounded-lg border border-[#262626] hover:border-[#333] cursor-pointer">
            <RadioGroupItem value="paypal" id="paypal" data-testid="payout-paypal" />
            <Label htmlFor="paypal" className="flex-1 cursor-pointer">
              <span className="font-medium">PayPal</span>
              <p className="text-sm text-[#A1A1AA]">Receive payments via PayPal (available worldwide)</p>
            </Label>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-surface-highlight rounded-lg border border-[#262626] hover:border-[#333] cursor-pointer">
            <RadioGroupItem value="stripe" id="stripe" data-testid="payout-stripe" />
            <Label htmlFor="stripe" className="flex-1 cursor-pointer">
              <span className="font-medium">Stripe Connect</span>
              <p className="text-sm text-[#A1A1AA]">Direct bank deposits (limited countries)</p>
            </Label>
          </div>
        </RadioGroup>
        {formData.payout_method === 'paypal' && (
          <div className="space-y-2">
            <Label htmlFor="paypal_email">PayPal Email *</Label>
            <Input id="paypal_email" type="email" value={formData.paypal_email} onChange={(e) => setFormData({ ...formData, paypal_email: e.target.value })} className="input-dark" placeholder="your@paypal.email" data-testid="setup-paypal-email" />
          </div>
        )}
        {formData.payout_method === 'stripe' && (
          <p className="text-sm text-[#A1A1AA] p-4 bg-surface-highlight rounded-lg">
            You'll be able to connect your Stripe account from your dashboard after completing setup.
          </p>
        )}
      </div>

      <Button type="submit" disabled={saving} className="w-full btn-primary" data-testid="complete-setup-btn">
        {saving ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Setting up...</>) : (<><CheckCircle className="w-4 h-4 mr-2" />Complete Setup</>)}
      </Button>
    </motion.form>
  );
}
